import React, { useState, useEffect } from 'react';
import './VesselUpload.css';

import { API_BASE } from '../../config';

const API_BASE_URL = `${API_BASE}/api`;

export const VesselUpload = ({ onClose, onUploadComplete }) => {
  const [uploadMode, setUploadMode] = useState('vessel'); // 'vessel' or 'sop'
  const [vessels, setVessels] = useState([]);
  const [selectedVessel, setSelectedVessel] = useState('');
  const [newVesselName, setNewVesselName] = useState('');
  const [isCreatingVessel, setIsCreatingVessel] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  
  // SOP Route specific fields
  const [routeName, setRouteName] = useState('');
  const [routeDescription, setRouteDescription] = useState('');

  // Fetch existing vessels on mount
  useEffect(() => {
    if (uploadMode === 'vessel') {
      fetchVessels();
    }
  }, [uploadMode]);

  const fetchVessels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/vessels`);
      if (!response.ok) throw new Error('Failed to fetch vessels');
      const data = await response.json();
      
      // Extract unique vessel names from raw_vessel_points
      const vesselNames = data.map(v => v.vessel_name).sort();
      setVessels(vesselNames);
    } catch (err) {
      console.error('Error fetching vessels:', err);
      setError('Failed to load vessel list');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setError(null);
    
    // Preview first file if CSV
    if (files.length > 0 && files[0].name.endsWith('.csv')) {
      previewFile(files[0]);
    }
    
    // Auto-populate route name from filename for SOP routes
    if (uploadMode === 'sop' && files.length > 0 && !routeName) {
      const fileName = files[0].name.replace(/\.(csv|json)$/i, '');
      const cleanName = fileName.replace(/[-_]/g, ' ');
      setRouteName(cleanName);
    }
  };

  const previewFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          setPreviewData({ error: 'File is empty or invalid' });
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim());
        const firstRow = lines[1].split(',').map(v => v.trim());
        
        setPreviewData({
          fileName: file.name,
          headers,
          firstRow,
          totalRows: lines.length - 1
        });
      } catch (err) {
        setPreviewData({ error: 'Failed to parse CSV' });
      }
    };
    reader.readAsText(file);
  };

  const handleUploadVesselData = async () => {
    const vesselName = isCreatingVessel ? newVesselName : selectedVessel;
    
    if (!vesselName) {
      setError('Please select or create a vessel');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress({ current: 0, total: selectedFiles.length, points: 0 });

    try {
      let totalPoints = 0;
      
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('vessel_name', vesselName);

        const response = await fetch(`${API_BASE_URL}/upload-points`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to upload ${file.name}`);
        }

        const result = await response.json();
        totalPoints += result.points_imported || 0;

        setUploadProgress({
          current: i + 1,
          total: selectedFiles.length,
          points: totalPoints
        });
      }

      // Success
      setTimeout(() => {
        if (onUploadComplete) {
          onUploadComplete({ vessel: vesselName, points: totalPoints });
        }
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
      setUploading(false);
    }
  };

  const handleUploadSOPRoute = async () => {
    if (!routeName.trim()) {
      setError('Please enter a route name');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress({ current: 0, total: 1, waypoints: 0 });

    try {
      const file = selectedFiles[0]; // SOP routes are uploaded one at a time
      const formData = new FormData();
      formData.append('file', file);
      formData.append('route_name', routeName.trim());
      if (routeDescription.trim()) {
        formData.append('description', routeDescription.trim());
      }

      const response = await fetch(`${API_BASE_URL}/upload-sop-route`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to upload SOP route`);
      }

      const result = await response.json();
      
      setUploadProgress({
        current: 1,
        total: 1,
        waypoints: result.waypoints || 0,
        distance: result.distance_nm || 0
      });

      // Success
      setTimeout(() => {
        if (onUploadComplete) {
          onUploadComplete({ 
            routeName: result.route_name, 
            waypoints: result.waypoints,
            distance: result.distance_nm
          });
        }
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
      setUploading(false);
    }
  };

  const handleUpload = () => {
    if (uploadMode === 'vessel') {
      handleUploadVesselData();
    } else {
      handleUploadSOPRoute();
    }
  };

  const getVesselOption = () => {
    if (isCreatingVessel) {
      return (
        <div className="vessel-input-group">
          <input
            type="text"
            className="vessel-name-input"
            placeholder="Enter new vessel name..."
            value={newVesselName}
            onChange={(e) => setNewVesselName(e.target.value)}
            autoFocus
          />
          <button 
            className="vessel-toggle-btn"
            onClick={() => {
              setIsCreatingVessel(false);
              setNewVesselName('');
            }}
          >
            Cancel
          </button>
        </div>
      );
    }

    return (
      <div className="vessel-input-group">
        <select
          className="vessel-select"
          value={selectedVessel}
          onChange={(e) => setSelectedVessel(e.target.value)}
        >
          <option value="">-- Select Existing Vessel --</option>
          {vessels.map(vessel => (
            <option key={vessel} value={vessel}>{vessel}</option>
          ))}
        </select>
        <button 
          className="vessel-toggle-btn"
          onClick={() => setIsCreatingVessel(true)}
        >
          + New
        </button>
      </div>
    );
  };

  return (
    <div className="upload-overlay">
      <div className="upload-modal">
        <div className="upload-header">
          <h2>Upload Data</h2>
          <button className="upload-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="upload-body">
          {/* Upload Mode Toggle */}
          <div className="upload-section">
            <div className="upload-mode-toggle">
              <button
                className={`mode-btn ${uploadMode === 'vessel' ? 'active' : ''}`}
                onClick={() => {
                  setUploadMode('vessel');
                  setSelectedFiles([]);
                  setError(null);
                  setPreviewData(null);
                }}
                disabled={uploading}
              >
                Vessel Tracking Data
              </button>
              <button
                className={`mode-btn ${uploadMode === 'sop' ? 'active' : ''}`}
                onClick={() => {
                  setUploadMode('sop');
                  setSelectedFiles([]);
                  setError(null);
                  setPreviewData(null);
                }}
                disabled={uploading}
              >
                SOP Route
              </button>
            </div>
          </div>

          {/* Vessel Data Upload */}
          {uploadMode === 'vessel' && (
            <>
              <div className="upload-section">
                <label className="upload-label">Vessel</label>
                {getVesselOption()}
              </div>

              <div className="upload-section">
                <label className="upload-label">
                  GPS Data Files (CSV)
                </label>
                <input
                  type="file"
                  accept=".csv"
                  multiple
                  onChange={handleFileSelect}
                  className="upload-file-input"
                  disabled={uploading}
                />
                {selectedFiles.length > 0 && (
                  <div className="upload-file-list">
                    <strong>{selectedFiles.length} file(s) selected:</strong>
                    <ul>
                      {selectedFiles.map((file, idx) => (
                        <li key={idx}>{file.name} ({(file.size / 1024).toFixed(1)} KB)</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}

          {/* SOP Route Upload */}
          {uploadMode === 'sop' && (
            <>
              <div className="upload-section">
                <label className="upload-label">Route Name *</label>
                <input
                  type="text"
                  className="route-name-input"
                  placeholder="e.g., Bellingham to Friday Harbor"
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  disabled={uploading}
                />
              </div>

              <div className="upload-section">
                <label className="upload-label">Description (Optional)</label>
                <textarea
                  className="route-description-input"
                  placeholder="e.g., Primary summer route via Lopez Pass"
                  value={routeDescription}
                  onChange={(e) => setRouteDescription(e.target.value)}
                  rows={2}
                  disabled={uploading}
                />
              </div>

              <div className="upload-section">
                <label className="upload-label">
                  Waypoints File (CSV/JSON)
                </label>
                <input
                  type="file"
                  accept=".csv,.json"
                  onChange={handleFileSelect}
                  className="upload-file-input"
                  disabled={uploading}
                />
                {selectedFiles.length > 0 && (
                  <div className="upload-file-list">
                    <strong>Selected:</strong> {selectedFiles[0].name} ({(selectedFiles[0].size / 1024).toFixed(1)} KB)
                  </div>
                )}
                <div className="upload-hint">
                  File must contain latitude and longitude columns. Optional: waypoint names, notes.
                </div>
              </div>
            </>
          )}

          {/* Preview */}
          {previewData && !previewData.error && (
            <div className="upload-section upload-preview">
              <h4>Preview: {previewData.fileName}</h4>
              <div className="preview-info">
                <span><strong>Total Rows:</strong> {previewData.totalRows}</span>
              </div>
              <div className="preview-table">
                <table>
                  <thead>
                    <tr>
                      {previewData.headers.slice(0, 6).map((header, idx) => (
                        <th key={idx}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {previewData.firstRow.slice(0, 6).map((value, idx) => (
                        <td key={idx}>{value}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="upload-error">
              <strong>⚠️ Error:</strong> {error}
            </div>
          )}

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="upload-progress">
              <div className="progress-info">
                {uploadMode === 'vessel' ? (
                  <>
                    <span>Uploading file {uploadProgress.current} of {uploadProgress.total}</span>
                    <span><strong>{uploadProgress.points}</strong> points imported</span>
                  </>
                ) : (
                  <>
                    <span>Importing SOP route...</span>
                    <span><strong>{uploadProgress.waypoints}</strong> waypoints • <strong>{uploadProgress.distance?.toFixed(1)}</strong> NM</span>
                  </>
                )}
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-bar-fill"
                  style={{ 
                    width: `${(uploadProgress.current / uploadProgress.total) * 100}%` 
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="upload-footer">
          <button 
            className="upload-btn upload-btn-cancel"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </button>
          <button 
            className="upload-btn upload-btn-upload"
            onClick={handleUpload}
            disabled={uploading || selectedFiles.length === 0}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VesselUpload;